// src/utils/errorHandling.js
import * as Sentry from "@sentry/react";

/**
 * Capture an error and send to Sentry with context
 * Use this in try-catch blocks for important operations
 */
export const captureError = (error, context = {}) => {
  console.error('Error captured:', error, context);
  
  // Send to Sentry with extra context
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Capture a message (not an error, but something important)
 * Use for warnings or unexpected states
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  console.log(`[${level}] ${message}`, context);
  
  Sentry.captureMessage(message, {
    level: level, // 'info', 'warning', 'error'
    extra: context,
  });
};

/**
 * Set user context for Sentry
 * Call this when user logs in
 */
export const setUserContext = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.uid,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Add breadcrumb - helpful for tracking user actions before an error
 * Sentry automatically adds many, but you can add custom ones
 */
export const addBreadcrumb = (message, category, data = {}) => {
  Sentry.addBreadcrumb({
    message: message,
    category: category, // e.g. 'file-upload', 'export', 'auth'
    data: data,
    level: 'info',
  });
};

/**
 * Wrap async functions to automatically catch and report errors
 */
export const withErrorTracking = (fn, context = {}) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureError(error, {
        ...context,
        functionName: fn.name,
        args: args,
      });
      throw error; // Re-throw so calling code can handle it
    }
  };
};

/**
 * Track performance of an operation
 * Uses Sentry v8+ span API
 */
export const trackOperation = async (operationName, fn) => {
  return await Sentry.startSpan(
    {
      name: operationName,
      op: 'function',
    },
    async (span) => {
      try {
        const result = await fn();
        span?.setStatus({ code: 1 }); // OK status
        return result;
      } catch (error) {
        span?.setStatus({ code: 2 }); // Error status
        captureError(error, { operation: operationName });
        throw error;
      }
    }
  );
};